// @workflow tasks.show-in-3d
/**
 * The facility deep link that shows tags: `/:facilityId?tagId=…&isolate=true | zoom360=true`, and,
 * for several tags (Khalid, 2026-10-14: a task opened in another facility shows ALL its tags
 * there), `…&tagIds=a,b,c&isolate=true`. Built by `navigateToFacility` (Task/utils/taskIsolation.js),
 * read by MainView (`handleTagSpatialNavigationFromUrl`) and the chip bridge (Assistant linkIntent.js).
 *
 * - One tag builds exactly today's link; the server's links (aiva.api AvaUiLinks.cs) stay valid.
 * - Several tags keep `tagId` as the first one, so a page that knows only `tagId` still shows it.
 * - 360 opens one scan, so it always carries one tag.
 * - At most MAX_TAG_IDS tags, and the whole link at most MAX_URL_LENGTH characters: past either,
 *   the FIRST tags that fit go, in order, and `tagTotal` says how many the task has, so the page
 *   tells the user it shows N of M (linkedTagsShortfallText). Not even two fit: today's one-tag link.
 * No imports, so `npm run test:unit` (node --test) can load it directly.
 */

export const TAG_LINK_PARAM = Object.freeze({ TAG_ID: 'tagId', TAG_IDS: 'tagIds', TAG_TOTAL: 'tagTotal' });

/** The deep link's viewer flags (MainView handleTagSpatialNavigationFromUrl). */
export const TAG_LINK_ACTION = Object.freeze({ ISOLATE: 'isolate', ZOOM_360: 'zoom360' });

export const TAG_LINK_LIMITS = Object.freeze({
  /** Tags one link carries (GUIDs: about 37 characters each). */
  MAX_TAG_IDS: 50,
  /** The longest link built, well inside what browsers and proxies keep. */
  MAX_URL_LENGTH: 2000,
  /** An id longer than this is not a tag id (the UI tools' limit, viewerContract.js). */
  MAX_TAG_ID_LENGTH: 64,
});

const SEPARATOR = ',';
const FLAG_ON = 'true';

/** A trimmed tag id that can travel in the list, or null. */
function cleanId(value) {
  if (typeof value !== 'string') return null;
  const id = value.trim();
  return id && id.length <= TAG_LINK_LIMITS.MAX_TAG_ID_LENGTH && !id.includes(SEPARATOR) ? id : null;
}

/** Usable ids, trimmed, in order, each once (compared without case). */
function cleanIds(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((value) => {
    const id = cleanId(value);
    if (id === null || seen.has(id.toLowerCase())) return;
    seen.add(id.toLowerCase());
    out.push(id);
  });
  return out;
}

function oneTagQuery(id, action, total) {
  const query = { [TAG_LINK_PARAM.TAG_ID]: id, [action]: FLAG_ON };
  return total > 1 ? { ...query, [TAG_LINK_PARAM.TAG_TOTAL]: String(total) } : query;
}

function severalTagsQuery(ids, total) {
  const query = {
    [TAG_LINK_PARAM.TAG_ID]: ids[0],
    [TAG_LINK_PARAM.TAG_IDS]: ids.join(SEPARATOR),
    [TAG_LINK_ACTION.ISOLATE]: FLAG_ON,
  };
  return total > ids.length ? { ...query, [TAG_LINK_PARAM.TAG_TOTAL]: String(total) } : query;
}

/**
 * The query of the link that shows `tagIds` with `action`.
 * @param {string[]} tagIds in the order to keep when not all fit
 * @param {string} action a TAG_LINK_ACTION value
 * @param {(query: object) => string} hrefOf the full link for a query (router.resolve(…).href)
 * @returns {object|null} null when no id is usable
 */
export function tagLinkQuery(tagIds, action, hrefOf) {
  const ids = cleanIds(tagIds);
  if (!ids.length) return null;
  if (ids.length === 1) return oneTagQuery(ids[0], action, 1);
  if (action !== TAG_LINK_ACTION.ISOLATE) return oneTagQuery(ids[0], action, 1);
  for (let count = Math.min(ids.length, TAG_LINK_LIMITS.MAX_TAG_IDS); count > 1; count -= 1) {
    const query = severalTagsQuery(ids.slice(0, count), ids.length);
    if (hrefOf(query).length <= TAG_LINK_LIMITS.MAX_URL_LENGTH) return query;
  }
  return oneTagQuery(ids[0], action, ids.length);
}

/**
 * The tags a link asks for: `tagIds` when it has any usable one, else `tagId`; never more than
 * MAX_TAG_IDS. `total` is the task's tag count the link reported, never below what it carries.
 * @param {object|null} query the route query
 * @returns {{ tagIds: string[], total: number }}
 */
export function tagIdsFromQuery(query) {
  const list = query && typeof query[TAG_LINK_PARAM.TAG_IDS] === 'string' ? query[TAG_LINK_PARAM.TAG_IDS].split(SEPARATOR) : [];
  let tagIds = cleanIds(list).slice(0, TAG_LINK_LIMITS.MAX_TAG_IDS);
  if (!tagIds.length && query) tagIds = cleanIds([query[TAG_LINK_PARAM.TAG_ID]]);
  const reported = query ? Number.parseInt(query[TAG_LINK_PARAM.TAG_TOTAL], 10) : NaN;
  return { tagIds, total: Number.isFinite(reported) ? Math.max(reported, tagIds.length) : tagIds.length };
}

/** What the facility page tells the user when the link carried only some of the task's tags. */
export function linkedTagsShortfallText(shown, total) {
  return `Showing ${shown} of the task's ${total} tags: a link can carry only ${shown}.`;
}
